/**
 * The complete HTTP surface: 50 operations across 45 paths, grouped exactly as
 * docs/03 §3 groups them.
 *
 * Three of them are never called by the web client and are present so the
 * surface is complete and reviewable rather than half-documented:
 * `GET /health`, `GET /series/{id}` and `POST /orders/batch` (§3.10).
 *
 * The whole API is built from one `HttpClient` so a test can hand it a fetch
 * that never touches a network, and so there is exactly one place that owns
 * caching, idempotency and the 401 path.
 */

import type { HttpClient } from '../client'
import { authEndpoints } from './auth'
import { chatEndpoints } from './chat'
import { growthEndpoints } from './growth'
import { indexEndpoints } from './index-prices'
import { marketEndpoints } from './markets'
import { metaEndpoints } from './meta'
import { notificationEndpoints } from './notifications'
import { portfolioEndpoints } from './portfolio'
import { tradingEndpoints } from './trading'
import { walletEndpoints } from './wallet'

export function createApi(client: HttpClient) {
  return {
    client,
    meta: metaEndpoints(client),
    auth: authEndpoints(client),
    markets: marketEndpoints(client),
    index: indexEndpoints(client),
    trading: tradingEndpoints(client),
    portfolio: portfolioEndpoints(client),
    wallet: walletEndpoints(client),
    growth: growthEndpoints(client),
    notifications: notificationEndpoints(client),
    chat: chatEndpoints(client),
  }
}

export type Api = ReturnType<typeof createApi>

export type { AuthEndpoints } from './auth'
export type { ChatEndpoints } from './chat'
export type { GrowthEndpoints } from './growth'
export type { IndexEndpoints } from './index-prices'
export type { MarketEndpoints } from './markets'
export type { MetaEndpoints } from './meta'
export type { NotificationEndpoints } from './notifications'
export type { PortfolioEndpoints } from './portfolio'
export type { SignedOrderOptions, TradingEndpoints } from './trading'
export type { WalletEndpoints } from './wallet'
